/**
 * LotSubmittedViewerDialog (Feb 2026)
 *
 * Read-only viewer for a Dispatch Lot's already-submitted Send-to-Finance
 * data. Distinct from `SendToFinanceDialog` (the writable submit form) and
 * from the Eye "View lot details" summary dialog.
 *
 * Renders exactly what was persisted to `lot.dispatch_header` — never
 * re-derived from `/prefill-context`, so the view reflects the actual
 * submitted values even if defaults have since drifted.
 *
 * Visibility (enforced by parent): only shown when `lot.status` is
 * PENDING_FINANCE or later — not DRAFT, not CANCELLED.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { FileText } from "lucide-react";
import AddressBlock from "./AddressBlock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Local INR formatter — mirrors `fmtINR` on `Dispatch.js` so the
// viewer's totals panel uses identical output (₹1,240.00) and the
// computed-value assertions in
// `test_lot_submitted_viewer_totals.py` are exact.
const _INR = new Intl.NumberFormat("en-IN", {
  style: "currency", currency: "INR",
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});
const fmtINR = (n) => _INR.format(Number(n || 0));

const STATUS_LABELS = {
  PENDING_FINANCE: "Pending Finance",
  INVOICED: "Invoiced",
  DISPATCHED: "Dispatched",
  RECEIVED: "Received",
  CLOSED: "Closed",
};

function statusLabel(status) {
  return STATUS_LABELS[status] || status || "Submitted";
}

export default function LotSubmittedViewerDialog({ open, onClose, lot }) {
  const navigate = useNavigate();
  const hdr = (lot && lot.dispatch_header) || {};
  const transporter = hdr.transporter || {};
  const discount = hdr.discount || { type: "percentage", value: 0 };
  const freight = hdr.freight_lines || [];

  // Jul 2026 — fetch the DETAIL endpoint on open so line items are
  // enriched with `unit_price` (resolved live from price_master by
  // `dispatch_lots_v2_routes.get_lot`, keyed on
  // `customer_id + buyer_sku_id + effective_to=None` — the same
  // source every other rate lookup in Dispatch Lots uses). Falls
  // back silently to the raw `lot.lines` if the fetch errors so the
  // rest of the viewer stays functional. Rate is NEVER persisted onto
  // `dispatch_header` — only computed at view time.
  const [enrichedLines, setEnrichedLines] = useState(null);
  useEffect(() => {
    if (!open || !lot || !lot.id) {
      setEnrichedLines(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/dispatch-lots-v2/${lot.id}`);
        if (!cancelled && res.data && Array.isArray(res.data.lines)) {
          setEnrichedLines(res.data.lines);
        }
      } catch (_e) {
        // Silent fallback — viewer keeps rendering; totals will read
        // from `lot.lines` which typically lacks `unit_price`, so the
        // total collapses to freight + zero-sub-total.
      }
    })();
    return () => { cancelled = true; };
  }, [open, lot]);

  const lines = enrichedLines || (lot && lot.lines) || [];

  // Discount/freight ordering (Jul 2026 P2, confirmed with user):
  // Discount applies to the SKU-line sub-total ONLY. Freight is
  // added afterward at cost, undiscounted. Rationale: Send-to-Finance
  // captures `freight_lines[]` as a structurally separate array from
  // `lines[]` (goods) — the two are billed independently. Confirmed
  // no existing precedent contradicts this: `Dispatch.js` treats
  // freight as a synthetic line inside `lines[]` (different model);
  // no backend path persists a `grand_total` on `dispatch_header`.
  const { subTotal, discountAmount, freightTotal, grandTotal } = useMemo(() => {
    const sub = lines.reduce((acc, l) => {
      const qty = Number(l.quantity || 0);
      const rate = Number(l.unit_price || 0);
      return acc + qty * rate;
    }, 0);
    const dv = Number(discount.value || 0);
    const dAmt = (discount.type === "amount") ? dv : (sub * dv / 100);
    const fr = freight.reduce((a, f) => a + Number(f.amount || 0), 0);
    return {
      subTotal: sub,
      discountAmount: dAmt,
      freightTotal: fr,
      grandTotal: sub - dAmt + fr,
    };
  }, [lines, discount.type, discount.value, freight]);

  // Feb 2026 — the standalone Dispatch-Lots-side invoice flow was
  // retired. Finance's next step lives on the Dispatch Invoice screen
  // against the lot's `linked_dispatch_invoice_id`. Deep-link handled
  // by a `?open=<id>` param that Dispatch.js reads to auto-open the
  // edit dialog for that specific invoice. Button is gated on the
  // linked id being present (AC4).
  const linkedInvoiceId = lot && lot.linked_dispatch_invoice_id;
  const goToDispatchInvoice = () => {
    if (!linkedInvoiceId) return;
    onClose();
    navigate(`/dispatch?open=${encodeURIComponent(linkedInvoiceId)}`);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="lot-submitted-viewer-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono" data-testid="lot-submitted-viewer-title">
            Submitted Dispatch Data — {statusLabel(lot && lot.status)}
          </DialogTitle>
          <DialogDescription>
            Read-only view of what was submitted for lot{" "}
            <span className="font-mono">{lot ? lot.lot_number : ""}</span>.
            Sourced from the lot&apos;s persisted <code>dispatch_header</code>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* BUG-019 — Row order matches the Send-to-Finance form:
              customer + source_branch header, then Bill-To / Ship-To,
              then Transporter with the Vehicle Number grouped
              alongside it (same as the form), then Freight Lines. */}
          {/* Row 1 — customer PO + source branch */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Customer PO</Label>
              <Input value={hdr.customer_po || ""} disabled data-testid="viewer-customer-po" />
            </div>
            <div>
              <Label className="text-xs">Source Branch</Label>
              <Input value={hdr.source_branch || ""} disabled data-testid="viewer-source-branch" />
            </div>
          </div>

          {/* Row 2 — bill-to / ship-to */}
          <div className="grid grid-cols-2 gap-3">
            <AddressBlock label="Bill To" addr={hdr.bill_to} testIdPrefix="viewer-bill-to" />
            <AddressBlock label="Ship To" addr={hdr.ship_to} testIdPrefix="viewer-ship-to" />
          </div>

          {/* Row 3 — transporter (with Vehicle Number grouped, matching the form) */}
          <div className="border border-zinc-200 rounded-sm p-3 bg-zinc-50/60" data-testid="viewer-transporter-block">
            <div className="text-xs font-mono uppercase text-zinc-500 mb-2">Transporter</div>
            <div className="grid grid-cols-4 gap-2">
              <div>
                <Label className="text-[10px]">Name</Label>
                <Input value={transporter.name || ""} disabled data-testid="viewer-transporter-name" />
              </div>
              <div>
                <Label className="text-[10px]">Vendor ID</Label>
                <Input value={transporter.vendor_id || ""} disabled data-testid="viewer-transporter-vendor-id" />
              </div>
              <div>
                <Label className="text-[10px]">GST</Label>
                <Input value={transporter.gst || ""} disabled data-testid="viewer-transporter-gst" />
              </div>
              <div>
                <Label className="text-[10px]">Vehicle Number</Label>
                <Input value={hdr.vehicle_number || ""} disabled data-testid="viewer-vehicle-number" />
              </div>
            </div>
          </div>

          {/* Row 4 — freight lines */}
          <div className="border border-zinc-200 rounded-sm p-3" data-testid="viewer-freight-block">
            <div className="text-xs font-mono uppercase text-zinc-500 mb-2">
              Freight Lines ({freight.length})
            </div>
            {freight.length === 0 ? (
              <div className="text-xs text-zinc-500 italic" data-testid="viewer-freight-empty">
                No freight lines submitted.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="p-2 text-left font-mono text-xs text-zinc-500">Description</th>
                    <th className="p-2 text-right font-mono text-xs text-zinc-500">Amount</th>
                    <th className="p-2 text-right font-mono text-xs text-zinc-500">GST %</th>
                  </tr>
                </thead>
                <tbody>
                  {freight.map((f, i) => (
                    <tr key={i} className="border-t border-zinc-100" data-testid={`viewer-freight-row-${i}`}>
                      <td className="p-2">
                        <Input value={f.description || ""} disabled data-testid={`viewer-freight-desc-${i}`} />
                      </td>
                      <td className="p-2 text-right">
                        <Input value={String(f.amount ?? 0)} disabled className="text-right font-mono" data-testid={`viewer-freight-amount-${i}`} />
                      </td>
                      <td className="p-2 text-right">
                        <Input value={String(f.gst_rate ?? 0)} disabled className="text-right font-mono" data-testid={`viewer-freight-gst-${i}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Row 5 — notes (discount was consolidated into the totals
              panel below the SKU lines per Jul 2026 P2 layout fix). */}
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea value={hdr.notes || ""} disabled rows={3} data-testid="viewer-notes" />
          </div>

          {/* Row 6 — SKU lines */}
          <div className="border border-zinc-200 rounded-sm p-3" data-testid="viewer-lines-block">
            <div className="text-xs font-mono uppercase text-zinc-500 mb-2">
              SKU Lines ({lines.length})
            </div>
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="p-2 text-left font-mono text-xs text-zinc-500">Buyer SKU</th>
                  <th className="p-2 text-left font-mono text-xs text-zinc-500">Description</th>
                  <th className="p-2 text-right font-mono text-xs text-zinc-500">Qty</th>
                  <th className="p-2 text-right font-mono text-xs text-zinc-500">Rate</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="border-t border-zinc-100" data-testid={`viewer-line-row-${i}`}>
                    <td className="p-2 font-mono">{l.buyer_sku_id || l.sku_id || "-"}</td>
                    <td className="p-2 text-zinc-600" data-testid={`viewer-sku-desc-${i}`}>
                      {l.sku_name || l.sku_description || l.description || "-"}
                    </td>
                    <td className="p-2 text-right font-mono">{Number(l.quantity || 0).toLocaleString()}</td>
                    <td className="p-2 text-right font-mono">{l.unit_price != null ? Number(l.unit_price).toLocaleString(undefined, { maximumFractionDigits: 2 }) : "-"}</td>
                  </tr>
                ))}
                {lines.length === 0 && (
                  <tr><td colSpan={4} className="p-4 text-center text-zinc-500 text-xs italic">No lines.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Row 7 — compact right-justified totals panel (Jul 2026 P2).
              Mirrors the shape used on `Dispatch.js`'s Create Dispatch
              form. Values computed live at view time — never persisted
              onto `dispatch_header`. Rates resolved via the detail
              endpoint's `price_master` enrichment (see the effect at
              the top of this component). */}
          <div
            className="border border-zinc-200 rounded-sm p-3 bg-zinc-50/60"
            data-testid="viewer-totals-block"
          >
            <div className="text-xs font-mono uppercase text-zinc-500 mb-2">Totals</div>
            <div className="max-w-md ml-auto space-y-1">
              <div className="grid grid-cols-[1fr_auto] gap-x-4 items-center">
                <div className="text-xs text-zinc-600 text-right">Sub Total</div>
                <div className="font-mono text-right text-sm text-zinc-800" data-testid="viewer-sub-total">
                  {fmtINR(subTotal)}
                </div>
                <div className="text-xs text-zinc-600 text-right">
                  Discount
                  {discount.type === "percentage" && discount.value ? ` (${Number(discount.value)}%)` : ""}
                </div>
                <div className="font-mono text-right text-sm text-red-600" data-testid="viewer-discount-amount">
                  −{fmtINR(discountAmount)}
                </div>
                <div className="text-xs text-zinc-600 text-right">
                  Freight
                  {freight.length ? ` (${freight.length} line${freight.length === 1 ? "" : "s"})` : ""}
                </div>
                <div className="font-mono text-right text-sm text-zinc-800" data-testid="viewer-freight-total">
                  {fmtINR(freightTotal)}
                </div>
                <div className="text-sm font-semibold text-zinc-800 text-right border-t border-zinc-300 pt-2 mt-1">
                  Grand Total
                </div>
                <div
                  className="font-mono text-right text-base font-bold text-zinc-900 border-t border-zinc-300 pt-2 mt-1"
                  data-testid="viewer-grand-total"
                >
                  {fmtINR(grandTotal)}
                </div>
              </div>
              <div className="text-[10px] text-zinc-500 italic text-right pt-2">
                Discount applies to line items only; freight billed at cost.
                Rates resolved live from Price Master at view time.
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          {/* BUG-019 — the earlier CTA that navigated to the dispatch invoice was removed.
              The viewer is now a pure read-only inspection of the
              submitted dispatch data; editing happens on the invoice
              screen, reached from the dispatch-invoices list. */}
          <Button variant="outline" onClick={onClose} data-testid="viewer-close-btn">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
